import { Badge, Box, Collapsible, HStack, Icon, Link, Text } from '@chakra-ui/react'
import { Tooltip } from '@src/components/chakra-ui/tooltip'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { stripMarkdownWhitespace } from '@src/components/sco/Chat/ChatMessageComposer'
import {
  CommentAttachmentChips,
  COMMENT_STORED_FILES_SELECTION,
} from '@src/components/sco/Comments/CommentAttachments'
import { CommentComposer } from '@src/components/sco/Comments/CommentComposer'
import { CommentDeleter } from '@src/components/sco/Comments/CommentDeleter'
import { CommentEditor } from '@src/components/sco/Comments/CommentEditor'
import { CommentList } from '@src/components/sco/Comments/CommentList'
import { LikeButton } from '@src/components/sco/LikeButton'
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { ToggleCommentFeaturedButton } from '@src/components/sco/ToggleCommentFeaturedButton'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { ScoComment } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import moment from 'moment'
import { useParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { IoReturnDownForwardOutline } from 'react-icons/io5'
import { LuPencil, LuReply, LuTrash } from 'react-icons/lu'

export type CommentCardProps = {
  isCollapsed?: boolean
  markdownPreset?: string
  onDeleted?: () => void
  onEdited?: () => void
  onFlagged?: () => void
  onReplyChange?: () => void
  showEditor?: boolean
  showDeleter?: boolean
  showFlagger?: boolean
  showReplies?: boolean
  showReplyComposer?: boolean
  comment: ScoComment
  featuredLabel?: string
}

export const CommentCard = (props: CommentCardProps) => {
  const { currentUser } = useCurrentUser()
  const [showEditor, setShowEditor] = useState(props.showEditor)
  const [showDeleter, setShowDeleter] = useState(props.showDeleter)
  const [showFlagger, setShowFlagger] = useState(props.showFlagger)
  const [showReplies, setShowReplies] = useState(props.showReplies)
  const [showReplyComposer, setShowReplyComposer] = useState(props.showReplyComposer)
  const [isCollapsed, setIsCollapsed] = useState(props.isCollapsed)
  const routerParams = useParams()

  const comment = props.comment
  const createdByUser = comment.createdByUser

  useEffect(() => {
    if (routerParams?.commentId) {
      const element = window?.document?.getElementById(`comment-${routerParams?.commentId}`)
      if (element) {
        const yOffset = -200
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }
  }, [routerParams?.commentId])

  return (
    <Collapsible.Root open={!comment.deletedAt}>
      <Collapsible.Content>
        <Box id={`comment-${props.comment.commentId}`}>
          {comment.canUserDelete && (
            <CommentDeleter
              commentId={comment.commentId}
              isOpen={showDeleter}
              onClose={() => setShowDeleter(false)}
            />
          )}
          {comment.canUserFlag && showFlagger && <>Flagger</>}

          <HStack
            align={'top'}
            gap={'12px'}
            paddingY={'10px'}
            {...(routerParams?.commentId === props.comment.commentId
              ? {
                  bgColor: 'rgba(0,0,0,.25)',
                  border: '1px solid',
                  borderColor: 'primary',
                  borderRadius: '8px',
                  paddingX: '10px',
                }
              : {})}
          >
            {/* AVATAR */}
            <Box>
              <UserAvatar avatarProps={{ size: 'sm' }} user={createdByUser} userId={createdByUser.userId} />
            </Box>

            {/* BODY */}
            <Box width={'full'}>
              {/* AUTHOR */}

              <HStack align={'center'} gap={4} paddingTop={'0.33em'}>
                <Link asChild display={'inline-block'} fontSize={'15px'} fontWeight={600}>
                  <NextLink href={`/members/${createdByUser.userId}`}>
                    {createdByUser.fullName || createdByUser.displayName}
                  </NextLink>
                </Link>
                {comment.featuredAt && (
                  <Badge
                    bgColor={'whiteAlpha.200'}
                    borderRadius={'md'}
                    color={'colorPalette.fg/50'}
                    fontFamily={'Poppins'}
                    fontSize={'11px'}
                    fontWeight={'bold'}
                  >
                    {props.featuredLabel || 'Featured Reply'}
                  </Badge>
                )}
              </HStack>

              {/* COMMENT BODY */}
              {comment.canUserEdit && showEditor && (
                <CommentEditor
                  comment={comment}
                  onCancel={() => setShowEditor(false)}
                  onError={() => setShowEditor(false)}
                  onSuccess={() => setShowEditor(false)}
                />
              )}
              {!showEditor && (
                <Collapsible.Root
                  open={!isCollapsed}
                  startingHeight={'100px'}
                  onClick={() => setIsCollapsed(false)}
                >
                  <Collapsible.Content>
                    <Box fontSize={'md'} paddingY={'0.5em'}>
                      {stripMarkdownWhitespace(comment.body || '') ? (
                        <MarkdownRenderer preset={props.markdownPreset || 'ugc'}>
                          {comment.body}
                        </MarkdownRenderer>
                      ) : null}
                      <CommentAttachmentChips files={comment.storedFiles?.items} />
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              )}

              {/* COMMENT FOOTER */}
              <HStack
                align={'center'}
                color={'primary'}
                gap={2}
                css={{
                  fontFamily: 'poppins',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {currentUser?.isAdmin && (
                  <Tooltip bgColor={'black'} content={'Feature'}>
                    <ToggleCommentFeaturedButton comment={comment} commentId={comment.commentId} />
                  </Tooltip>
                )}
                <Tooltip bgColor={'black'} content={'Like'}>
                  <LikeButton likeableId={comment.commentId} likeableType={'Comment'} />
                </Tooltip>

                <Tooltip bgColor={'black'} content={'Reply'}>
                  <Link
                    fontSize={'sm'}
                    _hover={{
                      color: 'white',
                      textDecoration: 'none',
                      transform: 'scale(1.2)',
                    }}
                    onClick={() => setShowReplyComposer(!showReplyComposer)}
                  >
                    <Icon asChild>
                      <LuReply />
                    </Icon>
                  </Link>
                </Tooltip>
                {comment.canUserEdit && (
                  <Tooltip bgColor={'black'} content={'Edit'}>
                    <Link
                      fontSize={'sm'}
                      _hover={{
                        color: 'white',
                        textDecoration: 'none',
                        transform: 'scale(1.2)',
                      }}
                      onClick={() => setShowEditor(!showEditor)}
                    >
                      <Icon asChild>
                        <LuPencil />
                      </Icon>
                    </Link>
                  </Tooltip>
                )}
                {comment.canUserDelete && (
                  <Tooltip bgColor={'black'} content={'Edit'}>
                    <Link
                      fontSize={'sm'}
                      _hover={{
                        color: 'white',
                        textDecoration: 'none',
                        transform: 'scale(1.2)',
                      }}
                      onClick={() => setShowDeleter(!showDeleter)}
                    >
                      <Icon asChild>
                        <LuTrash />
                      </Icon>
                    </Link>
                  </Tooltip>
                )}

                <Link asChild marginLeft={1} title={moment(comment.createdAt).format('LLL')}>
                  <NextLink href={`/comments/${comment.commentId}`}>
                    {moment(comment.createdAt).fromNow()}
                  </NextLink>
                </Link>
              </HStack>

              {showReplyComposer && (
                <Box borderColor={'primary'} borderLeft={'2px dashed)'}>
                  <PaywalledAction
                    contentPermission={'canAccessFGDiscussions'}
                    intent={'discussion-join'}
                    title={'Upgrade to join the discussion.'}
                    variant={'boxed'}
                  >
                    <CommentComposer
                      newCommentValues={{
                        parentCommentId: comment.commentId,
                        threadId: comment.threadId,
                      }}
                      onCancel={async () => {
                        await setShowReplyComposer(false)
                      }}
                      onSuccess={async () => {
                        await setShowReplies(true)
                        if (props.onReplyChange) {
                          await props.onReplyChange()
                        }
                        await setShowReplyComposer(false)
                      }}
                    />
                  </PaywalledAction>
                </Box>
              )}

              {showReplies && props.comment.childCommentsCount > 0 && (
                <Box borderLeft={'2px dashed rgba(255,255,255,.1)'} paddingLeft={'12px'}>
                  <CommentList
                    key={props.comment.childCommentsCount}
                    collapseComments={props.isCollapsed}
                    parentCommentId={props.comment.commentId}
                    showReplies={props.showReplies}
                    threadId={props.comment.threadId}
                  />
                  {/*<Link*/}
                  {/*  color={'rgba(255,255,255,.5)'}*/}
                  {/*  */}
                  {/*  fontSize={'12px'}*/}
                  {/*  onClick={() => setShowReplies(false)}*/}
                  {/*  textTransform={'uppercase'}*/}
                  {/*>*/}
                  {/*  Collapse*/}
                  {/*</Link>*/}
                </Box>
              )}

              {!showReplies && props.comment.childCommentsCount > 0 && (
                <Link
                  borderColor={'primaryAlpha.500'}
                  borderLeft={'2px dashed rgba(255,255,255,.1)'}
                  display={'block'}
                  fontSize={'12px'}
                  fontWeight={'bold'}
                  marginBottom={'12px'}
                  padding={'12px'}
                  onClick={() => setShowReplies(true)}
                >
                  <HStack>
                    <Icon asChild boxSize={'20px'} color={'primary'}>
                      <IoReturnDownForwardOutline />
                    </Icon>
                    <Text>
                      {props.comment.childCommentsCount}
                      {' Replies'}
                    </Text>
                  </HStack>
                </Link>
              )}
            </Box>
          </HStack>
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

CommentCard.fragments = {
  ScoComment: () => gql`
    fragment _ on ScoComment {
      id
      commentId
      createdByUser {
        id
        userId
        firstName
        fullName
        displayName
        lastName
        pictureUrl
      }
      featuredAt
      createdAt
      deletedAt
      body
      canUserComment
      canUserDelete
      canUserEdit
      canUserFlag
      canUserLike
      hasUserCommented
      hasUserFlagged
      hasUserLiked
      childCommentsCount
      parentCommentId
      threadId
      ${COMMENT_STORED_FILES_SELECTION}
    }
  `,
}
